import HeadlessLogin from "@/components/HeadlessLogin";
import { Button } from "@/components/ui/button";
import { useMediaQuery } from "@/lib/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { ArrowLeft, GraduationCap } from "@phosphor-icons/react";
import {
  StytchPasswordReset,
  useStytch,
  useStytchSession,
} from "@stytch/react";
import { Products, StytchLoginConfig } from "@stytch/vanilla-js";
import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PRIVACY_POLICY, TERMS_OF_SERVICE, ROOT } from "@/lib/links";
import { useInvalidateUserStats } from "@/lib/queries/user/useUserStats";
import { useTranslation } from "react-i18next";

function Login() {
  const { t } = useTranslation("auth");
  const isDesktop = useMediaQuery("(min-width: 640px)");
  const [searchParams, _] = useSearchParams();
  const returnTo = searchParams.get("returnTo") || "/";
  const invalidateUserStats = useInvalidateUserStats();

  const getConfig = (returnTo: string) => {
    const loginRedirectURL = `https://${import.meta.env.VITE_DOMAIN}/authenticate${returnTo !== "/" ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`;
    const resetPasswordRedirectURL = `https://${import.meta.env.VITE_DOMAIN}/authenticate/reset-password${returnTo !== "/" ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`;

    return {
      passwordOptions: {
        loginRedirectURL,
        resetPasswordRedirectURL,
        loginExpirationMinutes: 30,
        resetPasswordExpirationMinutes: 30,
      },
      emailMagicLinksOptions: {
        loginRedirectURL,
        loginExpirationMinutes: 30,
        signupRedirectURL: loginRedirectURL,
        signupExpirationMinutes: 30,
      },
      products: [Products.passwords, Products.emailMagicLinks],
      sessionOptions: {
        sessionDurationMinutes: 60 * 24 * 7,
      },
    } as StytchLoginConfig;
  };

  const stytch = useStytchSession();
  const stytchClient = useStytch();

  useEffect(() => {
    if (stytch.session !== null) {
      // Invalidate user stats when user logs in via magic link or OAuth
      invalidateUserStats();
      // Redirect to the original destination or home page
      window.location.href = decodeURIComponent(returnTo);
    }
  }, [stytch.session, returnTo, invalidateUserStats]);

  useEffect(() => {
    if (
      searchParams.has("token") &&
      searchParams.get("stytch_token_type") === "magic_links"
    ) {
      stytchClient.magicLinks.authenticate(searchParams.get("token") ?? "", {
        session_duration_minutes: 60 * 24,
      });
    } else if (
      searchParams.has("token") &&
      searchParams.get("stytch_token_type") === "login"
    ) {
      stytchClient.magicLinks.authenticate(searchParams.get("token") ?? "", {
        session_duration_minutes: 60 * 24,
      });
    } else if (
      searchParams.has("token") &&
      searchParams.get("stytch_token_type") === "oauth"
    ) {
      stytchClient.oauth.authenticate(searchParams.get("token") ?? "", {
        session_duration_minutes: 60 * 24,
      });
    }
  }, [searchParams, stytchClient]);

  if (
    searchParams.has("token") &&
    searchParams.get("stytch_token_type") === "reset_password"
  ) {
    return (
      <div className="flex flex-row w-full h-full">
        <div className="hidden w-full h-full bg-primary sm:block" />
        <main className="flex items-center justify-center w-full h-full">
          <StytchPasswordReset
            config={getConfig(returnTo)}
            passwordResetToken={searchParams.get("token") ?? ""}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-row w-full px-4 md:px-0 h-svh">
      <div className="relative hidden w-full h-full px-10 sm:block py-9">
        <img
          src={"assets/login-c.png"}
          className="object-cover w-full h-full rounded-[36px]"
        />
        <h1 className="absolute flex flex-row whitespace-nowrap gap-2 text-4xl font-semibold text-white bottom-24 right-12 text-[40px] xl:bottom-52 xl:right-44 xl:text-[79px] items-center">
          <GraduationCap size={60} />
          Teacher Tools
        </h1>
      </div>
      <main className="flex flex-col items-center justify-center w-full h-full">
        <Button variant="secondary" className="self-start mb-4 ml-4" asChild>
          <Link to={ROOT}>
            <ArrowLeft className="w-4 h-4" />
            {t("backToHome")}
          </Link>
        </Button>
        <h1
          className={cn(
            "text-4xl font-semibold text-left pl-12 text-primary ",
            isDesktop ? "w-[543px]" : "w-full"
          )}
        >
          {t("login.title")}
        </h1>
        <HeadlessLogin />

        <div className="flex flex-row items-center justify-center w-full gap-2 mt-4 text-sm font-normal text-light-grey">
          <Link
            to={PRIVACY_POLICY}
            className="text-sm font-normal underline bottom-4 text-light-grey decoration-dashed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            {t("login.privacyPolicy")}
          </Link>
          <Link
            to={TERMS_OF_SERVICE}
            className="text-sm font-normal underline bottom-4 text-light-grey decoration-dashed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            {t("login.termsOfService")}
          </Link>
        </div>
      </main>
    </div>
  );
}

export default Login;
